import type { DownloadJobType } from "@volgo/core/contract";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiClient } from "../api/client";
import { useConfig } from "../app/config";
import { isActive, pollUntil } from "./poll";

export type WatchState = "idle" | "watching" | "timeout" | "error";

/**
 * Follows the unfinished jobs in `jobs`: asks `downloads.get` for each of them once per interval and hands
 * every answer to `onJob`. Stops when none is unfinished, and in any case after `pollLimitMs`, also when a
 * progress request never answers (then `state` is "timeout" and `resume` starts a new, equally limited, round).
 */
export function useDownloadWatch(
  client: ApiClient,
  jobs: readonly DownloadJobType[],
  onJob: (job: DownloadJobType) => void,
): { state: WatchState; resume: () => void } {
  const { pollIntervalMs, pollLimitMs } = useConfig();
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const onJobRef = useRef(onJob);
  onJobRef.current = onJob;
  const [state, setState] = useState<WatchState>("idle");
  const [round, setRound] = useState(0);
  const hasActive = jobs.some(isActive);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` restarts the watch after a timeout
  useEffect(() => {
    if (!hasActive) {
      setState("idle");
      return;
    }
    const abort = new AbortController();
    setState("watching");
    pollUntil(
      async (wanted) => {
        const active = jobsRef.current.filter(isActive);
        const fresh = await Promise.all(
          active.map((job) => client.call("downloads.get", { params: { id: job.id } })),
        );
        // the watch ended or the time limit passed while this was on its way: the answer is dropped
        if (wanted.aborted) return false;
        for (const job of fresh) onJobRef.current(job);
        return fresh.some(isActive);
      },
      { intervalMs: pollIntervalMs, limitMs: pollLimitMs, signal: abort.signal },
    ).then(
      (end) => {
        if (end === "timeout") setState("timeout");
      },
      () => {
        if (!abort.signal.aborted) setState("error");
      },
    );
    return () => abort.abort();
  }, [hasActive, round, client, pollIntervalMs, pollLimitMs]);

  const resume = useCallback(() => setRound((r) => r + 1), []);
  return { state, resume };
}
